<template>
  <div style="position: relative">
    <v-card flat>
      <div id="controlsContainer">
        <div
          class="card-title table-controls px-3 d-flex align-center justify-space-between"
        >
          <div>
            <!-- CRUD buttons -->
            <!-- dialog for creating item -->
            <crud-button
              v-if="$checkPermission('TIMESLOTS')"
              x-large
              color="light-blue lighten-2"
              @clicked="create()"
              icon="fa-plus"
              :tooltip="$t('global.crud.button.add')"
            />
          </div>
          <!-- center side -->
          <div>
            <!-- Search by fields -->
            <!-- Search in table -->
            <v-container id="filterContainer">
              <v-row>
                <v-col sm="4" lg="2" xl="2" cols="12">
                  <v-text-field
                    :label="$t('timeSlots.name')"
                    v-model="filter.name"
                    clearable
                  ></v-text-field>
                </v-col>
                <v-col sm="4" lg="2" xl="2" cols="12">
                  <v-dialog
                    ref="dialogFilterTimeSlotFrom"
                    v-model="modalFilterTimeSlotFrom"
                    :return-value.sync="filter.hourFromString"
                    persistent
                    width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="filter.hourFromString"
                        :label="$t('timeSlots.timeSlotFrom')"
                        readonly
                        clearable
                        append-icon="far fa-clock"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-time-picker
                      v-if="modalFilterTimeSlotFrom"
                      v-model="filter.hourFromString"
                      full-width
                      format="24hr"
                      :allowed-minutes="allowedStepTime"
                      @click:minute="
                        $refs.dialogFilterTimeSlotFrom.save(
                          filter.hourFromString
                        )
                      "
                    >
                      <v-spacer></v-spacer>
                      <v-btn
                        text
                        color="primary"
                        @click="modalFilterTimeSlotFrom = false"
                      >
                        {{ $t("global.cancel") }}
                      </v-btn>
                    </v-time-picker>
                  </v-dialog>
                </v-col>
                <v-col sm="4" lg="2" xl="2" cols="12">
                  <v-dialog
                    ref="dialogFilterTimeSlotTo"
                    v-model="modalFilterTimeSlotTo"
                    :return-value.sync="filter.hourToString"
                    persistent
                    width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="filter.hourToString"
                        :label="$t('timeSlots.timeSlotTo')"
                        readonly
                        clearable
                        append-icon="far fa-clock"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-time-picker
                      v-if="modalFilterTimeSlotTo"
                      v-model="filter.hourToString"
                      full-width
                      format="24hr"
                      :allowed-minutes="allowedStepTime"
                      @click:minute="
                        $refs.dialogFilterTimeSlotTo.save(filter.hourToString)
                      "
                    >
                      <v-spacer></v-spacer>
                      <v-btn
                        text
                        color="primary"
                        @click="modalFilterTimeSlotTo = false"
                      >
                        {{ $t("global.cancel") }}
                      </v-btn>
                    </v-time-picker>
                  </v-dialog>
                </v-col>
                <v-col sm="6" lg="3" xl="2" cols="12">
                  <v-text-field
                    v-model="filter.agencyCode"
                    :label="$t('zones.agency')"
                    v-if="!$userIsAdmin()"
                    disabled
                  ></v-text-field>
                  <v-autocomplete
                    v-model="filter.agencyCode"
                    v-if="$userIsAdmin()"
                    :items="agenciesFilterItems"
                    :loading="agenciesFilter.isLoading"
                    :search-input.sync="agenciesFilter.search"
                    hide-no-data
                    :no-data-text="$t('global.autocomplete.noDataFound')"
                    item-text="description"
                    item-value="code"
                    :label="$t('timeSlots.agency')"
                    :placeholder="$t('global.autocomplete.starTypingToSearch')"
                    @focus="agenciesFilter.isEdit = true"
                    @change="agenciesFilter.isEdit = false"
                    clearable
                  ></v-autocomplete>
                </v-col>
                <v-col sm="6" lg="3" xl="2" cols="12">
                  <v-autocomplete
                    v-model="filter.zoneCode"
                    :items="zonesFilterItems"
                    :loading="zonesFilter.isLoading"
                    :search-input.sync="zonesFilter.search"
                    hide-no-data
                    :no-data-text="$t('global.autocomplete.noDataFound')"
                    item-text="description"
                    item-value="code"
                    :label="$t('timeSlots.zone')"
                    :placeholder="$t('global.autocomplete.starTypingToSearch')"
                    @focus="zonesFilter.isEdit = true"
                    @change="zonesFilter.isEdit = false"
                    clearable
                  ></v-autocomplete>
                </v-col>
              </v-row>
              <v-row>
                <v-col sm="6" lg="3" xl="2" cols="12">
                  <v-dialog
                    v-model="modalFilterValidityDateTimeSlotFrom"
                    :close-on-content-click="false"
                    persistent
                    width="290px"
                    @keydown.esc="modalFilterValidityDateTimeSlotFrom = false"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="filter.validityDateFromFormatted"
                        :label="$t('timeSlots.validityDateFrom')"
                        append-icon="far fa-calendar-alt"
                        readonly
                        clearable
                        v-on="on"
                        @click:clear="filter.validityDateFrom = null"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="filter.validityDateFrom"
                      scrollable
                      @input="modalFilterValidityDateTimeSlotFrom = false"
                    >
                      <v-spacer></v-spacer>
                      <v-btn
                        text
                        color="primary"
                        @click="modalFilterValidityDateTimeSlotFrom = false"
                      >
                        {{ $t("global.cancel") }}
                      </v-btn>
                    </v-date-picker>
                  </v-dialog>
                </v-col>
                <v-col sm="6" lg="3" xl="2" cols="12">
                  <v-dialog
                    v-model="modalFilterValidityDateTimeSlotTo"
                    :close-on-content-click="false"
                    persistent
                    width="290px"
                    @keydown.esc="modalFilterValidityDateTimeSlotTo = false"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="filter.validityDateToFormatted"
                        :label="$t('timeSlots.validityDateTo')"
                        append-icon="far fa-calendar-alt"
                        readonly
                        clearable
                        v-on="on"
                        @click:clear="filter.validityDateTo = null"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="filter.validityDateTo"
                      scrollable
                      @input="modalFilterValidityDateTimeSlotTo = false"
                    >
                      <v-spacer></v-spacer>
                      <v-btn
                        text
                        color="primary"
                        @click="modalFilterValidityDateTimeSlotTo = false"
                      >
                        {{ $t("global.cancel") }}
                      </v-btn>
                    </v-date-picker>
                  </v-dialog>
                </v-col>
                <v-col sm="6" lg="2" xl="1" cols="12">
                  <v-select
                    v-model="filter.currentlyInForce"
                    :items="currentlyInForceValues"
                    :label="$t('timeSlots.currentlyInForce')"
                  ></v-select>
                </v-col>
                <v-col sm="6" lg="2" xl="1" cols="12">
                  <v-select
                    v-model="filter.deletedItems"
                    :items="deletedItemsValues"
                    :label="$t('global.status')"
                  ></v-select>
                </v-col>
                <v-col sm="6" lg="2" xl="1" cols="12" align-self="center">
                  <v-menu
                    :close-on-content-click="false"
                    max-height="50vh"
                    offset-y
                  >
                    <template v-slot:activator="{ on }">
                      <v-btn v-on="on" block outlined class="mb-3">
                        {{ $t("timeSlots.weekdays") }}
                      </v-btn>
                    </template>
                    <v-list tile shaped>
                      <v-list-item>
                        <v-switch
                          hide-details
                          :label="$t('timeSlots.monday')"
                          v-model="filter.monday"
                        ></v-switch>
                      </v-list-item>
                      <v-list-item>
                        <v-switch
                          hide-details
                          :label="$t('timeSlots.tuesday')"
                          v-model="filter.tuesday"
                        ></v-switch>
                      </v-list-item>
                      <v-list-item>
                        <v-switch
                          hide-details
                          :label="$t('timeSlots.wednesday')"
                          v-model="filter.wednesday"
                        ></v-switch>
                      </v-list-item>
                      <v-list-item>
                        <v-switch
                          hide-details
                          :label="$t('timeSlots.thursday')"
                          v-model="filter.thursday"
                        ></v-switch>
                      </v-list-item>
                      <v-list-item>
                        <v-switch
                          hide-details
                          :label="$t('timeSlots.friday')"
                          v-model="filter.friday"
                        ></v-switch>
                      </v-list-item>
                      <v-list-item>
                        <v-switch
                          hide-details
                          :label="$t('timeSlots.saturday')"
                          v-model="filter.saturday"
                        ></v-switch>
                      </v-list-item>
                      <v-list-item>
                        <v-switch
                          hide-details
                          :label="$t('timeSlots.sunday')"
                          v-model="filter.sunday"
                        ></v-switch>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </v-col>
              </v-row>
            </v-container>
            <!-- clear filters -->
          </div>
          <!-- right side -->
          <div>
            <!-- Refresh table -->
            <crud-button
              large
              color="blue"
              @clicked="refreshItemsView()"
              icon="fa-sync-alt"
              :tooltip="$t('global.crud.buttons.refreshTable')"
            />
          </div>
          <ExportExcel
            url="/timeSlots"
            :filter="this.filter"
            :filterExport="filterExport"
          />
          <ImportExcel
            url="/timeSlots"
            @done="search"
            :filterImport="filterImport"
            v-if="$checkPermission('TIMESLOTS')"
          />
        </div>
      </div>
      <div id="dataContainer">
        <!-- Table -->
        <v-data-table
          :headers="headers"
          :items="items"
          item-key="code"
          :options.sync="options"
          :page.sync="pagination.page"
          :server-items-length="pagination.totalItems"
          :no-results-text="$t('global.datatable.noMatchingResults')"
          :no-data-text="$t('global.datatable.noDataAvailable')"
          :footer-props="{
            'items-per-page-options': rowsPerPageItems,
            'show-first-last-page': true,
            'show-current-page': true
          }"
          :loading="loading"
          light
          multi-sort
          dense
        >
          <template v-slot:body="{ items }">
            <tbody>
              <tr v-for="item in items" :key="item.code">
                <td>
                  <!-- Edit record -->
                  <crud-button
                    v-if="$checkPermission('TIMESLOTS')"
                    x-small
                    color="orange"
                    icon="fa-pen"
                    :tooltip="$t('global.crud.buttons.edit')"
                    @clicked="edit(item.code)"
                  ></crud-button>

                  <!-- Delete record -->
                  <crud-button
                    v-if="
                      (item.deletionDate === undefined ||
                        item.deletionDate === null) &&
                        $checkPermission('TIMESLOTS')
                    "
                    x-small
                    color="red"
                    icon="fa-trash"
                    :tooltip="$t('global.crud.buttons.delete')"
                    @clicked="suspend(item.code)"
                  ></crud-button>

                  <!-- Active record -->
                  <crud-button
                    v-else-if="$checkPermission('TIMESLOTS')"
                    x-small
                    color="green"
                    icon="fa-redo"
                    :tooltip="$t('global.crud.buttons.restore')"
                    @clicked="restore(item.code)"
                  ></crud-button>
                </td>
                <td>
                  <v-tooltip right>
                    <template v-slot:activator="{ on }">
                      <span v-on="on">{{ item.agency.code }}</span>
                    </template>
                    <span>{{ item.agency.name }}</span>
                  </v-tooltip>
                </td>
                <td>
                  <v-tooltip right>
                    <template v-slot:activator="{ on }">
                      <span v-on="on">{{ item.zone.code }}</span>
                    </template>
                    <span>{{ item.zone.name }}</span>
                  </v-tooltip>
                </td>
                <td>{{ item.name }}</td>
                <td>
                  {{ minutesToHour(item.hourFrom) }} -
                  {{ minutesToHour(item.hourTo) }}
                </td>
                <td>
                  {{
                    transformDate(
                      item.validityDateFrom,
                      $t("global.dateFormat")
                    )
                  }}
                  -
                  {{
                    transformDate(item.validityDateTo, $t("global.dateFormat"))
                  }}
                </td>
                <td class="text-right">
                  {{ minutesFormat(item.avalibleDeliveryTime) }}
                </td>
                <td><BooleanToIcon :value="item.monday" /></td>
                <td><BooleanToIcon :value="item.tuesday" /></td>
                <td><BooleanToIcon :value="item.wednesday" /></td>
                <td><BooleanToIcon :value="item.thursday" /></td>
                <td><BooleanToIcon :value="item.friday" /></td>
                <td><BooleanToIcon :value="item.saturday" /></td>
                <td><BooleanToIcon :value="item.sunday" /></td>
              </tr>
            </tbody>
          </template>
        </v-data-table>
      </div>
    </v-card>
    <v-dialog v-model="dialog" max-width="600" persistent no-click-animation>
      <validation-observer ref="obs" v-slot="{ invalid, validated, passes }">
        <v-card>
          <v-toolbar dark color="primary" dense flat>
            <v-toolbar-title class="white--text">
              {{ formTitle }}
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon @click.native="close()">
              <v-icon>fas fa-times</v-icon>
            </v-btn>
          </v-toolbar>

          <v-card-text class="details-list">
            <v-form>
              <v-container>
                <v-row no-gutters>
                  <v-col>
                    <validation-provider
                      :name="$t('timeSlots.name')"
                      rules="required|max:60"
                      v-slot="{ errors }"
                    >
                      <v-text-field
                        :label="$t('timeSlots.name')"
                        v-model="item.name"
                        required
                        :error-messages="errors"
                        counter="60"
                      ></v-text-field>
                    </validation-provider>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col>
                    <v-dialog
                      v-model="modalValidityDateTimeSlotFrom"
                      :close-on-content-click="false"
                      persistent
                      width="290px"
                      @keydown.esc="modalValidityDateTimeSlotFrom = false"
                    >
                      <template v-slot:activator="{ on }">
                        <validation-provider
                          :name="$t('timeSlots.validityDateFrom')"
                          rules="required"
                          v-slot="{ errors }"
                          vid="fieldValidityDateFrom"
                        >
                          <v-text-field
                            v-model="item.validityDateFromFormatted"
                            :label="$t('timeSlots.validityDateFrom')"
                            append-icon="far fa-calendar-alt"
                            readonly
                            required
                            clearable
                            v-on="on"
                            :error-messages="errors"
                            @click:clear="item.validityDateFrom = null"
                          ></v-text-field>
                        </validation-provider>
                      </template>
                      <v-date-picker
                        v-model="item.validityDateFrom"
                        scrollable
                        @input="modalValidityDateTimeSlotFrom = false"
                      >
                        <v-spacer></v-spacer>
                        <v-btn
                          text
                          color="primary"
                          @click="modalValidityDateTimeSlotFrom = false"
                        >
                          {{ $t("global.cancel") }}
                        </v-btn>
                      </v-date-picker>
                    </v-dialog>
                  </v-col>
                  <v-col>
                    <v-dialog
                      v-model="modalValidityDateTimeSlotTo"
                      :close-on-content-click="false"
                      persistent
                      width="290px"
                      @keydown.esc="modalValidityDateTimeSlotTo = false"
                    >
                      <template v-slot:activator="{ on }">
                        <v-text-field
                          v-model="item.validityDateToFormatted"
                          :label="$t('timeSlots.validityDateTo')"
                          append-icon="far fa-calendar-alt"
                          readonly
                          clearable
                          v-on="on"
                          @click:clear="item.validityDateTo = null"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="item.validityDateTo"
                        scrollable
                        @input="modalValidityDateTimeSlotTo = false"
                      >
                        <v-spacer></v-spacer>
                        <v-btn
                          text
                          color="primary"
                          @click="modalValidityDateTimeSlotTo = false"
                        >
                          {{ $t("global.cancel") }}
                        </v-btn>
                      </v-date-picker>
                    </v-dialog>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <v-dialog
                      ref="dialogTimeSlotFrom"
                      v-model="modalTimeSlotFrom"
                      :return-value.sync="item.hourFromString"
                      persistent
                      width="290px"
                    >
                      <template v-slot:activator="{ on }">
                        <validation-provider
                          :name="$t('timeSlots.timeSlotFrom')"
                          rules="required"
                          v-slot="{ errors }"
                          vid="fieldTimeSlotFrom"
                        >
                          <v-text-field
                            v-model="item.hourFromString"
                            :label="$t('timeSlots.timeSlotFrom')"
                            readonly
                            required
                            append-icon="far fa-clock"
                            :error-messages="errors"
                            v-on="on"
                          ></v-text-field>
                        </validation-provider>
                      </template>
                      <v-time-picker
                        v-if="modalTimeSlotFrom"
                        v-model="item.hourFromString"
                        full-width
                        format="24hr"
                        :allowed-minutes="allowedStepTime"
                        @click:minute="
                          $refs.dialogTimeSlotFrom.save(item.hourFromString)
                        "
                      >
                        <v-spacer></v-spacer>
                        <v-btn
                          text
                          color="primary"
                          @click="modalTimeSlotFrom = false"
                        >
                          {{ $t("global.cancel") }}
                        </v-btn>
                        <v-btn
                          text
                          color="primary"
                          @click="
                            $refs.dialogTimeSlotFrom.save(item.hourFromString)
                          "
                        >
                          {{ $t("global.confirm") }}
                        </v-btn>
                      </v-time-picker>
                    </v-dialog>
                  </v-col>

                  <v-col>
                    <v-dialog
                      ref="dialogTimeSlotTo"
                      v-model="modalTimeSlotTo"
                      :return-value.sync="item.hourToString"
                      persistent
                      width="290px"
                    >
                      <template v-slot:activator="{ on }">
                        <validation-provider
                          :name="$t('timeSlots.timeSlotTo')"
                          rules="required|hourBefore:@fieldTimeSlotFrom"
                          vid="fieldTimeSlotTo"
                          v-slot="{ errors }"
                        >
                          <v-text-field
                            v-model="item.hourToString"
                            :label="$t('timeSlots.timeSlotTo')"
                            readonly
                            required
                            append-icon="far fa-clock"
                            :error-messages="errors"
                            v-on="on"
                          ></v-text-field>
                        </validation-provider>
                      </template>
                      <v-time-picker
                        v-if="modalTimeSlotTo"
                        v-model="item.hourToString"
                        full-width
                        format="24hr"
                        :allowed-minutes="allowedStepTime"
                        @click:minute="
                          $refs.dialogTimeSlotTo.save(item.hourToString)
                        "
                      >
                        <v-spacer></v-spacer>
                        <v-btn
                          text
                          color="primary"
                          @click="modalTimeSlotTo = false"
                        >
                          {{ $t("global.cancel") }}
                        </v-btn>
                        <v-btn
                          text
                          color="primary"
                          @click="
                            $refs.dialogTimeSlotTo.save(item.hourToString)
                          "
                        >
                          {{ $t("global.confirm") }}
                        </v-btn>
                      </v-time-picker>
                    </v-dialog>
                  </v-col>

                  <v-col>
                    <validation-provider
                      :name="$t('timeSlots.avalibleDeliveryTime')"
                      rules="required|max:5"
                      v-slot="{ errors }"
                    >
                      <v-text-field
                        :label="$t('timeSlots.avalibleDeliveryTime')"
                        v-model="item.avalibleDeliveryTime"
                        required
                        :error-messages="errors"
                        @keypress="integerOnlyField($event)"
                      ></v-text-field>
                    </validation-provider>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col>
                    <validation-provider
                      :name="$t('timeSlots.resourceNumberWeight')"
                      rules="decimal:7,4"
                      v-slot="{ errors }"
                    >
                      <v-text-field
                        :label="$t('timeSlots.resourceNumberWeight')"
                        v-model="item.resourceNumberWeight"
                        type="number"
                        @keypress="decimalOnlyField($event)"
                        :error-messages="errors"
                      ></v-text-field>
                    </validation-provider>
                  </v-col>

                  <v-col>
                    <validation-provider
                      :name="$t('timeSlots.resourceNumberVolumen')"
                      rules="decimal:5,4"
                      v-slot="{ errors }"
                    >
                      <v-text-field
                        :label="$t('timeSlots.resourceNumberVolumen')"
                        v-model="item.resourceNumberVolumen"
                        type="number"
                        :error-messages="errors"
                        @keypress="decimalOnlyField($event)"
                      ></v-text-field>
                    </validation-provider>
                  </v-col>

                  <v-col>
                    <validation-provider
                      :name="$t('timeSlots.resourceNumberSlots')"
                      rules="integer|max:10"
                      v-slot="{ errors }"
                    >
                      <v-text-field
                        :label="$t('timeSlots.resourceNumberSlots')"
                        v-model="item.resourceNumberSlots"
                        type="number"
                        :error-messages="errors"
                        @keypress="integerOnlyField($event)"
                      ></v-text-field>
                    </validation-provider>
                  </v-col>
                </v-row>

                <v-row no-gutters>
                  <v-col align="center">
                    {{ $t("timeSlots.monday") }}
                  </v-col>
                  <v-col align="center">
                    {{ $t("timeSlots.tuesday") }}
                  </v-col>
                  <v-col align="center">
                    {{ $t("timeSlots.wednesday") }}
                  </v-col>
                  <v-col align="center">
                    {{ $t("timeSlots.thursday") }}
                  </v-col>
                  <v-col align="center">
                    {{ $t("timeSlots.friday") }}
                  </v-col>
                  <v-col align="center">
                    {{ $t("timeSlots.saturday") }}
                  </v-col>
                  <v-col align="center">
                    {{ $t("timeSlots.sunday") }}
                  </v-col>
                </v-row>

                <v-row no-gutters>
                  <v-col class="d-inline-flex justify-center">
                    <v-switch v-model="item.monday" class="ml-2"></v-switch>
                  </v-col>
                  <v-col class="d-inline-flex justify-center">
                    <v-switch v-model="item.tuesday" class="ml-2"></v-switch>
                  </v-col>
                  <v-col class="d-inline-flex justify-center">
                    <v-switch v-model="item.wednesday" class="ml-2"></v-switch>
                  </v-col>
                  <v-col class="d-inline-flex justify-center">
                    <v-switch v-model="item.thursday" class="ml-2"></v-switch>
                  </v-col>
                  <v-col class="d-inline-flex justify-center">
                    <v-switch v-model="item.friday" class="ml-2"></v-switch>
                  </v-col>
                  <v-col class="d-inline-flex justify-center">
                    <v-switch v-model="item.saturday" class="ml-2"></v-switch>
                  </v-col>
                  <v-col class="d-inline-flex justify-center">
                    <v-switch v-model="item.sunday" class="ml-2"></v-switch>
                  </v-col>
                </v-row>

                <v-row no-gutters>
                  <v-col>
                    <validation-provider
                      v-if="!editMode"
                      :name="$t('timeSlots.agency')"
                      rules="required"
                      v-slot="{ errors }"
                    >
                      <v-text-field
                        v-model="item.agencyCode"
                        :label="$t('zones.agency')"
                        v-if="!$userIsAdmin()"
                        disabled
                      ></v-text-field>
                      <v-autocomplete
                        v-model="item.agencyCode"
                        v-if="$userIsAdmin()"
                        :items="agenciesItems"
                        :loading="agencies.isLoading"
                        :search-input.sync="agencies.search"
                        hide-no-data
                        :no-data-text="$t('global.autocomplete.noDataFound')"
                        item-text="description"
                        item-value="code"
                        :label="$t('timeSlots.agency')"
                        :placeholder="
                          $t('global.autocomplete.starTypingToSearch')
                        "
                        @focus="agencies.isEdit = true"
                        @change="
                          agencies.isEdit = false;
                          resetZonesSearch();
                        "
                        :error-messages="errors"
                        clearable
                      ></v-autocomplete>
                    </validation-provider>
                    <v-text-field
                      v-else
                      v-model="item.agencyString"
                      :label="$t('timeSlots.agency')"
                      disabled
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row no-gutters>
                  <v-col>
                    <validation-provider
                      v-if="!editMode"
                      :name="$t('timeSlots.zone')"
                      rules="required"
                      v-slot="{ errors }"
                    >
                      <v-autocomplete
                        v-model="item.zoneCode"
                        :items="zonesItems"
                        :loading="zones.isLoading"
                        :search-input.sync="zones.search"
                        hide-no-data
                        :disabled="isAgencySelected"
                        :no-data-text="$t('global.autocomplete.noDataFound')"
                        item-text="description"
                        item-value="code"
                        :label="$t('timeSlots.zone')"
                        :placeholder="
                          $t('global.autocomplete.starTypingToSearch')
                        "
                        @focus="zones.isEdit = true"
                        @change="zones.isEdit = false"
                        :error-messages="errors"
                        clearable
                      ></v-autocomplete>
                    </validation-provider>
                    <v-text-field
                      v-else
                      :label="$t('timeSlots.zone')"
                      v-model="item.zoneString"
                      disabled
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-container>
            </v-form>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="black" text @click.native="close()">
              {{ $t("global.buttons.close") }}
            </v-btn>
            <v-btn v-if="editMode" color="orange" text @click="passes(update)">
              {{ $t("global.buttons.update") }}
            </v-btn>
            <v-btn v-else color="green" text @click="passes(save)">
              {{ $t("global.buttons.create") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </validation-observer>
    </v-dialog>
  </div>
</template>

<script>
import CrudButton from "@/components/Button.vue";
import BooleanToIcon from "@/components/BooleanToIcon.vue";
import localhttp from "../js/https-commons";
import ExportExcel from "@/components/ExportExcel.vue";
import ImportExcel from "@/components/ImportExcel.vue";
import {
  hourToMinutes,
  minutesToHour,
  booleanToText,
  decimalOnlyField,
  integerOnlyField,
  minutesFormat,
  transformDate,
  dateToIso,
  excelTimeToMinutes
} from "@/js/utils.js";

const CONTEXT = "/timeSlots";
const CONTEXT_AGENCIES = "/agencies";
const CONTEXT_ZONES = "/zones";
const ENTITY_NAME = "timeSlot.";

export default {
  components: {
    CrudButton,
    BooleanToIcon,
    ExportExcel,
    ImportExcel
  },
  name: "TimeSlots",
  data: () => ({
    modalTimeSlotFrom: false,
    modalTimeSlotTo: false,
    modalValidityDateTimeSlotFrom: false,
    modalValidityDateTimeSlotTo: false,
    modalFilterTimeSlotFrom: false,
    modalFilterTimeSlotTo: false,
    modalFilterValidityDateTimeSlotFrom: false,
    modalFilterValidityDateTimeSlotTo: false,
    loading: false,
    searchTable: "",
    filter: {
      name: "",
      hourFrom: "",
      hourFromString: "",
      hourTo: "",
      hourToString: "",
      avalibleDeliveryTime: "",
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false,
      sunday: false,
      validityDateFrom: null,
      validityDateFromFormatted: "",
      validityDateTo: null,
      validityDateToFormatted: "",
      agencyCode: "",
      zoneCode: "",
      currentlyInForce: false,
      deletedItems: "ACTIVE"
    },
    editMode: false,
    dialog: false,
    options: {},
    pagination: {
      totalItems: 0,
      page: 1
    },
    rowsPerPageItems: [10, 20, 30, 40],
    item: {},
    items: [],
    agencies: {
      isLoading: false,
      entries: [],
      search: null,
      value: "",
      isEdit: false
    },
    agenciesFilter: {
      isLoading: false,
      entries: [],
      search: null,
      value: "",
      isEdit: false
    },
    zones: {
      isLoading: false,
      entries: [],
      search: null,
      value: "",
      isEdit: false
    },
    zonesFilter: {
      isLoading: false,
      entries: [],
      search: null,
      value: "",
      isEdit: false
    }
  }),
  computed: {
    deletedItemsValues: function() {
      return [
        { text: this.$t("global.status.all"), value: "ALL" },
        { text: this.$t("global.status.active"), value: "ACTIVE" },
        { text: this.$t("global.status.deleted"), value: "DELETED" }
      ];
    },
    currentlyInForceValues: function() {
      return [
        { text: this.$t("global.yes"), value: true },
        { text: this.$t("global.no"), value: false }
      ];
    },
    formTitle() {
      return this.editMode
        ? this.$t("timeSlots.title.edit")
        : this.$t("timeSlots.title.new");
    },
    headers() {
      return [
        { text: this.$t("global.datatable.header.actions"), sortable: false },
        {
          text: this.$t("timeSlots.datatable.agency"),
          value: ENTITY_NAME + "agencyCode"
        },
        {
          text: this.$t("timeSlots.datatable.zone"),
          value: ENTITY_NAME + "zoneCode"
        },
        {
          text: this.$t("timeSlots.datatable.name"),
          value: ENTITY_NAME + "name"
        },
        {
          text: this.$t("timeSlots.datatable.timeSlot"),
          value: ENTITY_NAME + "hourFrom"
        },
        {
          text: this.$t("timeSlots.datatable.validity"),
          value: ENTITY_NAME + "validityDateFrom"
        },
        {
          text: this.$t("timeSlots.datatable.avalibleDeliveryTime"),
          value: ENTITY_NAME + "avalibleDeliveryTime"
        },
        { text: this.$t("timeSlots.datatable.monday"), sortable: false },
        { text: this.$t("timeSlots.datatable.tuesday"), sortable: false },
        { text: this.$t("timeSlots.datatable.wednesday"), sortable: false },
        { text: this.$t("timeSlots.datatable.thursday"), sortable: false },
        { text: this.$t("timeSlots.datatable.friday"), sortable: false },
        { text: this.$t("timeSlots.datatable.saturday"), sortable: false },
        { text: this.$t("timeSlots.datatable.sunday"), sortable: false }
      ];
    },
    agenciesItems() {
      return this.agencies.entries.map(entry => {
        const description = entry.code + " - " + entry.name;
        return Object.assign({}, entry, { description });
      });
    },
    agenciesFilterItems() {
      return this.agenciesFilter.entries.map(entry => {
        const description = entry.code + " - " + entry.name;
        return Object.assign({}, entry, { description });
      });
    },
    zonesItems() {
      return this.zones.entries.map(entry => {
        const description = entry.code + " - " + entry.name;
        return Object.assign({}, entry, { description });
      });
    },
    zonesFilterItems() {
      return this.zonesFilter.entries.map(entry => {
        const description = entry.code + " - " + entry.name;
        return Object.assign({}, entry, { description });
      });
    },
    isAgencySelected() {
      return (
        this.item.agencyCode === undefined || this.item.agencyCode === null
      );
    }
  },
  watch: {
    options: {
      handler() {
        this.search();
      },
      deep: true
    },
    "pagination.totalItems": {
      handler() {
        this.pagination.page = 1;
      },
      deep: true
    },
    "filter.validityDateFrom": function() {
      this.filter.validityDateFromFormatted = this.transformDate(
        this.filter.validityDateFrom,
        this.$t("global.dateFormat")
      );
    },
    "filter.validityDateTo": function() {
      this.filter.validityDateToFormatted = this.transformDate(
        this.filter.validityDateTo,
        this.$t("global.dateFormat")
      );
    },
    "item.validityDateFrom": function() {
      this.item.validityDateFromFormatted = this.transformDate(
        this.item.validityDateFrom,
        this.$t("global.dateFormat")
      );
    },
    "item.validityDateTo": function() {
      this.item.validityDateToFormatted = this.transformDate(
        this.item.validityDateTo,
        this.$t("global.dateFormat")
      );
    },
    searchTable() {
      if (!this.searchTableWaiting) {
        setTimeout(() => {
          this.search();
          this.searchTableWaiting = false;
        }, 500);
      }
      this.searchTableWaiting = true;
    },
    "agencies.search": function(value) {
      console.debug("agencies.search");
      this.preLoadGenericSearchEntity(value, this.agencies, CONTEXT_AGENCIES);
    },
    "agenciesFilter.search": function(value) {
      console.debug("agenciesFilter.search");
      this.preLoadGenericSearchEntity(
        value,
        this.agenciesFilter,
        CONTEXT_AGENCIES
      );
    },
    "zones.search": function(value) {
      console.debug("zones.search");
      let aditionalFilter = {
        agencyCode: this.item.agencyCode
      };
      this.preLoadGenericSearchEntity(
        value,
        this.zones,
        CONTEXT_ZONES,
        aditionalFilter
      );
    },
    "zonesFilter.search": function(value) {
      console.debug("zonesFilter.search");
      let aditionalFilter = {
        agencyCode: this.filter.agencyCode
      };
      this.preLoadGenericSearchEntity(
        value,
        this.zonesFilter,
        CONTEXT_ZONES,
        aditionalFilter
      );
    }
  },
  methods: {
    search() {
      console.debug("Search elements");

      // Items have already been requested
      if (this.loading) return;

      this.loading = true;

      // Parse hour:time to minutes
      this.filter.hourFrom =
        this.filter.hourFromString != null
          ? hourToMinutes(this.filter.hourFromString)
          : null;
      this.filter.hourTo =
        this.filter.hourToString != null
          ? hourToMinutes(this.filter.hourToString)
          : null;

      const { sortBy, sortDesc, page, itemsPerPage } = this.options;
      this.filter.sortBy = sortBy;
      this.filter.sortDesc = sortDesc;
      this.filter.page = page;
      this.filter.itemsPerPage = itemsPerPage;

      localhttp
        .post(CONTEXT + "/allPagination", this.filter)
        .then(response => {
          this.pagination.totalItems = response.data.totalElements;
          this.items = response.data.content;
          this.loading = false;
        })
        .catch(e => {
          console.log(e);
          this.loading = false;
          this.$showError();
        });
    },
    preLoadGenericSearchEntity(value, object, context, aditionalFilter) {
      if (!object.isLoadingWaiting) {
        setTimeout(() => {
          this.genericSearchEntity(object, context, aditionalFilter);
          object.isLoadingWaiting = false;
        }, 500);
      }
      object.value = value;
      object.isLoadingWaiting = true;
    },
    async genericSearchEntity(object, context, aditionalFilter) {
      console.debug("genericSearchEntity");
      if (!object.isEdit || object.value === null || object.value === "")
        return;

      if (object.isLoading) return;

      object.isLoading = true;

      let filterGeneric = {
        allFields: object.value
      };
      if (aditionalFilter !== undefined) {
        Object.keys(aditionalFilter).forEach(
          key => (filterGeneric[key] = aditionalFilter[key])
        );
      }

      // Lazily load input items
      localhttp
        .post(context + "/allFilter", filterGeneric)
        .then(response => {
          console.debug(response.data);
          object.entries = response.data;
        })
        .catch(e => {
          console.log(e);
          this.$showError();
        })
        .finally(() => (object.isLoading = false));
    },
    create() {
      console.debug("Create element");
      this.editMode = false;
      this.resetDialog();
      this.item.agencyCode = this.$userIsAdmin()
        ? ""
        : this.$keycloak.tokenParsed.agency;
      this.dialog = true;
    },
    async save() {
      console.debug("Save form");

      this.item.hourFrom = hourToMinutes(this.item.hourFromString);
      this.item.hourTo = hourToMinutes(this.item.hourToString);

      localhttp
        .post(CONTEXT + "/create", this.item)
        .then(response => {
          console.log(response.data);
          this.close();
          this.search();
        })
        .catch(e => {
          console.log(e);
          this.$showErrorCode(e.response.status);
        });
    },
    edit(id) {
      console.debug("Edit element " + id);
      this.editMode = true;
      this.resetDialog();
      localhttp
        .get(CONTEXT + "/" + id)
        .then(response => {
          this.item = response.data;
          this.editPrepare(this.item);
          this.dialog = true;
        })
        .catch(e => {
          console.log(e);
          this.$showErrorCode(e.response.status);
        });
    },
    editPrepare(item) {
      item.hourFromString = minutesToHour(item.hourFrom);
      item.hourToString = minutesToHour(item.hourTo);
      item.agencyString = item.agency.code + " - " + item.agency.name;
      item.zoneString = item.zone.code + " - " + item.zone.name;
    },
    async update() {
      console.debug("Update form");

      this.item.hourFrom = hourToMinutes(this.item.hourFromString);
      this.item.hourTo = hourToMinutes(this.item.hourToString);

      localhttp
        .put(CONTEXT + "/update/" + this.item.code, this.item)
        .then(response => {
          console.log(response.data);
          this.close();
          this.search();
        })
        .catch(e => {
          console.log(e);
          this.$showErrorCode(e.response.status);
        });
    },
    async suspend(id) {
      console.debug("Suspend element " + id);
      if (
        await this.$root.$confirm(
          this.$t("global.confirm"),
          this.$t("global.action.suspend.confirm"),
          { color: "blue" }
        )
      ) {
        localhttp
          .put(CONTEXT + "/delete/" + id)
          .then(response => {
            console.log(response.data);
            this.search();
          })
          .catch(e => {
            console.log(e);
            this.$showErrorCode(e.response.status);
          });
      }
    },
    restore(id) {
      console.debug("Restore element " + id);
      localhttp
        .put(CONTEXT + "/activate/" + id)
        .then(response => {
          console.log(response.data);
          this.search();
        })
        .catch(e => {
          console.log(e);
          this.$showErrorCode(e.response.status);
        });
    },
    refreshItemsView() {
      console.debug("Refresh table");
      this.search();
    },
    close() {
      this.dialog = false;
    },
    resetDialog() {
      this.item = {};
      this.resetAgenciesSearch();
      this.resetZonesSearch();
      requestAnimationFrame(() => {
        if (this.$refs.obs !== undefined) {
          this.$refs.obs.reset();
        }
      });
    },
    allowedStepTime: m => m % 5 === 0,
    hourToMinutes,
    minutesToHour,
    decimalOnlyField,
    integerOnlyField,
    minutesFormat,
    transformDate,
    booleanToTextString(value) {
      return booleanToText(value, this.$t("global.yes"), this.$t("global.no"));
    },
    resetAgenciesSearch() {
      console.debug("resetAgenciesSearch");
      this.agencies.search = "";
      this.agencies.entries = [];
    },
    resetZonesSearch() {
      console.debug("resetZonesSearch");
      this.zones.search = "";
      this.zones.entries = [];
    },
    filterExport(jsonData) {
      var vm = this;
      jsonData.forEach(function(v) {
        delete v.agency;
        delete v.zone;
        v.hourFrom = minutesToHour(v.hourFrom);
        v.hourTo = minutesToHour(v.hourTo);
        v.validityDateFrom = transformDate(
          v.validityDateFrom,
          vm.$t("global.dateFormat")
        );
        v.validityDateTo = transformDate(
          v.validityDateTo,
          vm.$t("global.dateFormat")
        );
      });
    },
    filterImport(jsonData) {
      var vm = this;
      jsonData.forEach(function(v) {
        v.hourFrom = excelTimeToMinutes(v.hourFrom);
        v.hourTo = excelTimeToMinutes(v.hourTo);
        console.debug(v.validityDateTo);
        if (v.validityDateTo !== undefined && v.validityDateTo !== null) {
          v.validityDateTo = dateToIso(
            v.validityDateTo,
            vm.$t("global.dateFormat")
          );
        }
        v.validityDateFrom = dateToIso(
          v.validityDateFrom,
          vm.$t("global.dateFormat")
        );
      });
    }
  },
  mounted() {},
  beforeMount() {
    this.filter.agencyCode = this.$userIsAdmin()
      ? ""
      : this.$keycloak.tokenParsed.agency;
  }
};
</script>
